import React, {FunctionComponent, useEffect, useState} from "react";
import {Carousel} from "react-responsive-carousel";
import {
    BigPlayButton,
    ControlBar,
    CurrentTimeDisplay,
    ForwardControl,
    FullscreenToggle,
    PlaybackRateMenuButton,
    Player,
    ReplayControl,
    TimeDivider,
    VolumeMenuButton
} from "video-react";
import ScrollLock from 'react-scrolllock';
import "react-responsive-carousel/lib/styles/carousel.min.css";
import 'video-react/dist/video-react.css';
import MediaItem from "../models/media-item";
import LazyLoad from "react-lazyload"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faComment, faHeart} from "@fortawesome/free-solid-svg-icons";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../reducers/root-reducer";
import {CommentItem} from "../models/responses";
import {getCommentsForItem, publishComment} from "../store/media-album-actions";
import "./gallery.css"
import {Button, FormGroup, TextArea} from "@blueprintjs/core";
import {useForm} from "react-hook-form";
import {AppState} from "../reducers/current-user-reducer";

interface NewCommentFormData {
    message: string
}

const Gallery: FunctionComponent<{ images: MediaItem[] }> = ({images}) => {

    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [commentsIsOpen, setCommentIsOpen] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const comments = useSelector<RootState, CommentItem[]>(s => s.albumState.media.find(s => s.id === images[selectedIndex].id)?.comments || [])
    const {userCheckState} = useSelector<RootState, AppState>(s => s.appState)
    const dispatch = useDispatch()

    const {handleSubmit, register, errors, setValue} = useForm<NewCommentFormData>({mode: "onSubmit", reValidateMode: "onChange"})

    useEffect(() => {
        document.addEventListener("keyup", onKeyPress, false)

        return () => {
            document.removeEventListener("keyup", onKeyPress)
        }
    }, [])

    function onItemClickHandler(id: number, media: MediaItem) {
        setSelectedIndex(id);
        setModalIsOpen(true);
        dispatch(getCommentsForItem(images[id].id))
    }

    function toggleModal() {
        setModalIsOpen(!modalIsOpen)
    }

    function onKeyPress(e: KeyboardEvent) {
        if (e.key === "Escape") {
            setModalIsOpen(false)
        }
    }

    function onSlideChange(e: number, a: any) {
        dispatch(getCommentsForItem(images[e].id))
        setSelectedIndex(e)
        setValue([{"message": ""}])
    }

    function doPublishComment(formData: NewCommentFormData){
        dispatch(publishComment(images[selectedIndex].id, formData.message))
        setValue([{"message": ""}])
    }

    function toggleCommentPanel(){
        setCommentIsOpen(!commentsIsOpen)
    }

    return (
        <div>
            <div className="gallery-container">
                {images.map((media, i) =>
                    <LazyLoad key={i + media.thumbnail} height={150} placeholder={<div style={{height:"150px",width:"150px"}}/>} once resize={true} >
                    <div className="image" onClick={(e) => onItemClickHandler(i, media)}>
                        <img src={media.thumbnail} title={media.title} alt={media.title}/>
                        {media.type !== "ads" && <div className="additional-info">
                            <div>
                                <FontAwesomeIcon icon={faHeart} />&nbsp;
                                {media.likes_count}
                            </div>
                            <div>
                                <FontAwesomeIcon icon={faComment} />&nbsp;
                                {media.comments_count}
                            </div>
                        </div>}

                    </div>
                    </LazyLoad>
                )}
            </div>
            {
                modalIsOpen && (
                    <ScrollLock>
                        <div style={{backgroundColor:"black"}} className="gallery-fullscreen">
                            <div className="gallery-carousel">

                                <span className="close-button" onClick={toggleModal}>
                                    <svg role="presentation" viewBox="0 0 24 24">
                                        <path
                                            d="M18.984 6.422l-5.578 5.578 5.578 5.578-1.406 1.406-5.578-5.578-5.578 5.578-1.406-1.406 5.578-5.578-5.578-5.578 1.406-1.406 5.578 5.578 5.578-5.578z"/>
                                    </svg>
                                </span>
                                <span className="comment-button" onClick={toggleCommentPanel}>
                                    <FontAwesomeIcon icon={faComment} />
                                </span>
                                <Carousel useKeyboardArrows={true} showThumbs={false} infiniteLoop={true}
                                          selectedItem={selectedIndex} showStatus={false} showIndicators={false}
                                          onChange={onSlideChange}>
                                    {
                                        images.map((media, i) =>
                                            < div key={"img" + i}>
                                                {media.type !== "video"
                                                    ? i === selectedIndex &&
                                                    <img src={media.filename} alt="" className="carousel-image"/>
                                                    : i === selectedIndex && <div className="player-container">
                                                    <Player
                                                        playsInline
                                                        poster={media.thumbnail}
                                                        src={media.filename}
                                                    >
                                                        <BigPlayButton position="center"/>
                                                        <ControlBar autoHide>
                                                            <ReplayControl seconds={10} order={1.1}/>
                                                            <ForwardControl seconds={30} order={1.2}/>
                                                            <VolumeMenuButton/>
                                                            <CurrentTimeDisplay order={4.1}/>
                                                            <TimeDivider order={4.2}/>
                                                            <PlaybackRateMenuButton rates={[5, 2, 1, 0.5, 0.1]}
                                                                                    order={7.1}/>
                                                            <FullscreenToggle disabled/>
                                                        </ControlBar>
                                                    </Player>
                                                </div>
                                                }
                                            </div>
                                        )
                                    }
                                </Carousel>
                            </div>
                            {commentsIsOpen && <div className={"gallery-comments"}>
                                {userCheckState == "logged" && <div style={{textAlign:"right"}}>
                                    <FormGroup>
                                        <TextArea inputRef={register({
                                            required: "Required",
                                        })} placeholder="Message" name="message"
                                                  style={{width:"100%"}}
                                                  onSubmit={handleSubmit(doPublishComment)} />
                                        {errors.message &&
                                            <span className={"bp3-intent-danger"}>
                                                {errors.message.message}
                                            </span>}
                                    </FormGroup>
                                    <Button onClick={handleSubmit(doPublishComment)}
                                            small
                                            disabled={errors.message!==undefined} >Publish comment</Button>
                                </div> }
                                {comments.map((comment) =>
                                    <div key={comment.id} className="gallery-comment">
                                        <div style={{fontWeight: "bold"}}>{comment.author}</div>
                                        <div>{comment.message}</div>
                                    </div>
                                )}
                            </div> }
                        </div>
                    </ScrollLock>
                )
            }
        </div>
    );
}

export default Gallery;